import { useState, useEffect, useRef } from 'react';
import './AnalyticsTab.css';
import { supabase } from './supabase';
import { calcAnalytics, loadAndMergeTradesFromSupabase } from './tradeUtils';
import { useEffectiveUser } from './ImpersonationContext';
import Badge from './components/Badge';
import LiveDot from './components/LiveDot';
import StatCard from './components/StatCard';

function timeAgo(ts) {
  const diff = Math.floor(Date.now() / 1000) - ts;
  if (diff < 60)    return 'à l\'instant';
  if (diff < 3600)  return `il y a ${Math.floor(diff / 60)}min`;
  if (diff < 86400) return `il y a ${Math.floor(diff / 3600)}h`;
  return `il y a ${Math.floor(diff / 86400)}j`;
}

//  Lit les trades du bot depuis localStorage
const loadBotTrades = () => {
  try {
    const saved = localStorage.getItem('bot-trades');
    return saved ? JSON.parse(saved) : [];
  } catch { return []; }
};

//  Structure d'un trade bot :
// { id, symbol, mint, entreeTs, sortieTs, achatUsd, venteUsd, pnlUsd, roi, roiPos, resultat, walletSuivi, txHash }

export default function AnalyticsTab({ onCopyWallet }) {
  const [period, setPeriod] = useState('30D');
  const [sortBy, setSortBy] = useState('date');
  const [trades, setTrades] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [expandedWallet, setExpandedWallet] = useState(null);
  const [hypotheticalAmount, setHypotheticalAmount] = useState('');
  const [copiedAddr, setCopiedAddr] = useState(null);

  const { effectiveUserId, isImpersonating } = useEffectiveUser();

  useEffect(() => {
    const load = async () => {
      try {
        const merged = await loadAndMergeTradesFromSupabase(effectiveUserId, isImpersonating);
        if (merged) setTrades(merged);
        else setTrades(isImpersonating ? [] : loadBotTrades());
      } catch { setTrades(loadBotTrades()); }
    };
    load();
    const iv = setInterval(load, 30000);
    let sub = null;
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session?.user) return;
      const targetId = effectiveUserId || session.user.id;
      sub = supabase.channel(`analytics-trades-${targetId}`)
        .on('postgres_changes', { event: '*', schema: 'public', table: 'trades',
          filter: `user_id=eq.${targetId}` }, load)
        .subscribe();
    });
    return () => { clearInterval(iv); sub?.unsubscribe(); };
  }, [effectiveUserId]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!session?.user) return;
      const targetId = effectiveUserId || session.user.id;
      supabase.from('configs').select('name, emoji, target_wallet')
        .eq('user_id', targetId)
        .then(({ data }) => {
          if (data) setConfigs(data.map(c => ({ name: c.name, emoji: c.emoji, targetWallet: c.target_wallet })));
        });
    });
  }, [effectiveUserId]);

  // Filtrer par période (sortieTs en priorité sur entreeTs)
  const now = Date.now() / 1000;
  const periodeMap = { '1D': 86400, '7D': 7*86400, '30D': 30*86400, '90D': 90*86400, 'ALL': Infinity };
  const filtered = trades.filter(t => {
    const ts  = t.sortieTs || t.entreeTs || 0;
    const age = now - ts;
    return age <= (periodeMap[period] || Infinity);
  });

  // simTradeOverride — what-if: recalcule le PNL sim avec un montant personnalisé
  const simTradeOverride = parseFloat(localStorage.getItem('sim-trade-amount') || '0');
  const filteredDisplay  = simTradeOverride > 0
    ? filtered.map(t => t.isSim && t.achatUsd > 0
        ? { ...t, pnlUsd: t.pnlUsd != null ? t.pnlUsd * (simTradeOverride / t.achatUsd) : null, achatUsd: simTradeOverride }
        : t)
    : filtered;

  // Stats via calcAnalytics (exclut 'En cours' et 'Fermé (historique)')
  const stats        = calcAnalytics(filteredDisplay);
  const totalTrades  = stats?.total        ?? 0;
  const gagnants     = stats?.winners      ?? 0;
  const perdants     = stats?.losers       ?? 0;
  const pnlTotal     = stats?.totalPnl     ?? 0;
  const totalInvesti   = stats?.totalInvested ?? 0;
  const capitalEnCours = filtered.filter(t => t.resultat === 'En cours').reduce((s,t) => s + (t.achatUsd || 0), 0);
  const roi          = stats?.totalPnlPct  ?? 0;
  const winRate      = stats?.winRate      ?? 0;
  const gainMoy      = stats?.gainMoy      ?? 0;
  const perteMoy     = stats?.perteMoy     ?? 0;

  const fmtUsd = (n) => {
    const sign = n >= 0 ? (n > 0 ? '+': '') : '-';
    const abs = Math.abs(n);
    if (abs >= 1e6) return `${sign}$${(abs/1e6).toFixed(2)}M`;
    if (abs >= 1e3) return `${sign}$${(abs/1e3).toFixed(1)}K`;
    return `${sign}$${abs.toFixed(0)}`;
  };

  // Trier les trades
  const sorted = [...filteredDisplay].sort((a, b) => {
    if (sortBy === 'date')    return (b.entreeTs||0) - (a.entreeTs||0);
    if (sortBy === 'pnl')     return (b.pnlUsd||0) - (a.pnlUsd||0);
    if (sortBy === 'roi')     return (b.roi||0) - (a.roi||0);
    return 0;
  });

  // PNL cumulatif pour le graphique
  const pnlCurve = (() => {
    let cumul = 0;
    return [...filteredDisplay].sort((a,b) => (a.sortieTs||a.entreeTs||0)-(b.sortieTs||b.entreeTs||0)).map(t => {
      cumul += t.pnlUsd || 0;
      const ts = t.sortieTs || t.entreeTs;
      return { cumul, date: ts ? new Date(ts*1000).toLocaleDateString('fr-FR',{day:'2-digit',month:'2-digit'}) : ''};
    });
  })();

  const pnlCurveSim = (() => {
    let cumul = 0;
    return [...filteredDisplay].filter(t=>t.isSim&&t.resultat!=='En cours')
      .sort((a,b)=>(a.sortieTs||a.entreeTs||0)-(b.sortieTs||b.entreeTs||0))
      .map(t=>{cumul+=t.pnlUsd||0;const ts=t.sortieTs||t.entreeTs;return{cumul,date:ts?new Date(ts*1000).toLocaleDateString('fr-FR',{day:'2-digit',month:'2-digit'}):''};});
  })();
  const pnlCurveLive = (() => {
    let cumul = 0;
    return [...filteredDisplay].filter(t=>!t.isSim&&t.resultat!=='En cours')
      .sort((a,b)=>(a.sortieTs||a.entreeTs||0)-(b.sortieTs||b.entreeTs||0))
      .map(t=>{cumul+=t.pnlUsd||0;const ts=t.sortieTs||t.entreeTs;return{cumul,date:ts?new Date(ts*1000).toLocaleDateString('fr-FR',{day:'2-digit',month:'2-digit'}):''};});
  })();

  // Export CSV
  const exportCSV = () => {
    const headers = ['Date','Token','Wallet Copié','Config','SIM/LIVE','Investi ($)','Vente ($)','PNL ($)','ROI (%)','Résultat','Raison'];
    const rows = sorted.map(t => {
      const cfg = configs.find(c => c.targetWallet === t.walletSuivi);
      const closeLabel = t.closeReason === 'STOP-LOSS' ? 'SL' : t.closeReason === 'TAKE-PROFIT' ? 'TP' : t.closeReason === 'TRAILING-STOP' ? 'TSL' : t.closeReason === 'MANUAL' ? 'Manuel' : t.closeReason || '';
      return [
        t.entreeTs ? new Date(t.entreeTs*1000).toLocaleDateString('fr-FR') : '',
        t.symbol || '',
        t.walletSuivi || '',
        cfg ? `${cfg.emoji ? cfg.emoji + ' ' : ''}${cfg.name}` : '',
        t.isSim ? 'SIM' : 'LIVE',
        (t.achatUsd || 0).toFixed(2),
        (t.venteUsd || 0).toFixed(2),
        (t.pnlUsd || 0).toFixed(2),
        t.roi !== undefined ? t.roi.toFixed(1) : '',
        t.resultat || '',
        closeLabel,
      ];
    });
    const csv = [headers, ...rows].map(r => r.map(v => `"${v}"`).join(',')).join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `trades_${new Date().toLocaleDateString('fr-FR').replace(/\//g,'-')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Wallets les plus copiés
  const walletStats = {};
  filteredDisplay.forEach(t => {
    if (!t.walletSuivi) return;
    if (!walletStats[t.walletSuivi]) walletStats[t.walletSuivi] = { trades:0, pnl:0, wins:0, bestPnl:-Infinity, worstPnl:Infinity, invested:0, roiList:[] };
    walletStats[t.walletSuivi].trades++;
    walletStats[t.walletSuivi].pnl += t.pnlUsd || 0;
    walletStats[t.walletSuivi].invested += t.achatUsd || 0;
    if (t.resultat === 'Gagnant') walletStats[t.walletSuivi].wins++;
    const p = t.pnlUsd || 0;
    if (p > walletStats[t.walletSuivi].bestPnl) walletStats[t.walletSuivi].bestPnl = p;
    if (p < walletStats[t.walletSuivi].worstPnl) walletStats[t.walletSuivi].worstPnl = p;
    walletStats[t.walletSuivi].roiList.push(t.roi || 0);
  });
  const topWallets = Object.entries(walletStats).sort((a,b) => b[1].pnl - a[1].pnl);

  const todayStart = new Date(); todayStart.setHours(0,0,0,0);
  const todayStartTs = todayStart.getTime() / 1000;
  const todayTrades  = trades.filter(t => (t.entreeTs||0) >= todayStartTs);
  const todayOpen    = todayTrades.filter(t => t.resultat === 'En cours');
  const todayClosed  = todayTrades.filter(t => t.resultat === 'Gagnant' || t.resultat === 'Perdant');
  const todaySimPnl  = todayClosed.filter(t => t.isSim).reduce((s,t) => s+(t.pnlUsd||0), 0);
  const todayLivePnl = todayClosed.filter(t => !t.isSim).reduce((s,t) => s+(t.pnlUsd||0), 0);
  const todayBest  = todayClosed.reduce((b,t) => !b||(t.roi||0)>(b.roi||0)?t:b, null);
  const todayWorst = todayClosed.reduce((w,t) => !w||(t.roi||0)<(w.roi||0)?t:w, null);

  const pnlChartRef = useRef(null);
  const heatmapRef  = useRef(null);
  const enterFS = r => { const el=r?.current; (el?.requestFullscreen||el?.webkitRequestFullscreen||el?.mozRequestFullScreen)?.call(el); };

  const thisWeekStart=(()=>{const d=new Date();d.setHours(0,0,0,0);d.setDate(d.getDate()-((d.getDay()+6)%7));return d.getTime()/1000;})();
  const calcWStats=tList=>{const cl=tList.filter(t=>t.resultat!=='En cours');const pnl=cl.reduce((s,t)=>s+(t.pnlUsd||0),0);const wins=cl.filter(t=>t.resultat==='Gagnant').length;return{n:cl.length,pnl,winRate:cl.length>0?(wins/cl.length)*100:0};};
  const thisWk=calcWStats(trades.filter(t=>(t.sortieTs||t.entreeTs||0)>=thisWeekStart));
  const lastWk=calcWStats(trades.filter(t=>{const ts=t.sortieTs||t.entreeTs||0;return ts>=thisWeekStart-7*86400&&ts<thisWeekStart;}));

  // Heatmap — PNL par jour
  const dayPnl = {};
  filteredDisplay.forEach(t=>{
    if(t.resultat!=='En cours'){
      const ts=t.sortieTs||t.entreeTs;
      if(ts){const key=new Date(ts*1000).toLocaleDateString('fr-FR');dayPnl[key]=(dayPnl[key]||0)+(t.pnlUsd||0);}
    }
  });

  return (
    <div className="an-root">

      {/* Period filter */}
      <div className="an-period-bar">
        <div className="an-periods">
          {['1D','7D','30D','90D','ALL'].map(p=>(
            <button key={p} className={`an-period ${period===p?'active':''}`} onClick={()=>setPeriod(p)}>{p==='1D'?'Auj.':p}</button>
          ))}
        </div>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <LiveDot color={trades.length>0?'accent':'inactive'} size={8} pulse={trades.length>0} />
          <span style={{fontSize:11,color:'#5a6a80'}}>{trades.length > 0 ? `${trades.length} trade(s) bot`: 'En attente du bot'}</span>
        </div>
      </div>

      {/* Section Aujourd'hui */}
      {todayTrades.length > 0 && (
        <div style={{padding:'9px 16px',background:'linear-gradient(135deg,rgba(0,229,204,0.03),rgba(124,77,255,0.03))',borderBottom:'1px solid #1a2030',display:'flex',gap:14,flexWrap:'wrap',alignItems:'center'}}>
          <span style={{fontSize:10,color:'#00e5cc',fontWeight:700,letterSpacing:'0.8px',whiteSpace:'nowrap'}}>AUJOURD'HUI</span>
          <span style={{fontSize:10,color:'#5a6a80'}}>{todayOpen.length} en cours</span>
          <span style={{fontSize:10,color:'#5a6a80'}}>{todayClosed.length} fermés</span>
          {todaySimPnl!==0&&<span style={{fontSize:10,color:'#7c4dff',fontWeight:700}}>SIM {todaySimPnl>=0?'+':''}{todaySimPnl.toFixed(2)}$</span>}
          {todayLivePnl!==0&&<span style={{fontSize:10,color:todayLivePnl>=0?'#00e5cc':'#ff6b6b',fontWeight:700}}>LIVE {todayLivePnl>=0?'+':''}{todayLivePnl.toFixed(2)}$</span>}
          {todaySimPnl===0&&todayLivePnl===0&&todayClosed.length===0&&<span style={{fontSize:10,color:'#3a4a5a'}}>Aucun trade clôturé</span>}
          {todayBest&&<span style={{fontSize:10,color:'#3a4a5a'}}> <span style={{color:'#00e5cc',fontWeight:700}}>{todayBest.symbol} +{(todayBest.roi||0).toFixed(0)}%</span></span>}
          {todayWorst&&todayWorst.symbol!==todayBest?.symbol&&<span style={{fontSize:10,color:'#3a4a5a'}}> <span style={{color:'#ff6b6b',fontWeight:700}}>{todayWorst.symbol} {(todayWorst.roi||0).toFixed(0)}%</span></span>}
        </div>
      )}

      {/* Stats cards */}
      <div className="an-stat-cards">
        <StatCard
          label="TRADES EXÉCUTÉS"
          value={totalTrades}
          sub={`${gagnants} gagnants · ${perdants} perdants`}
          trend="neutral"
          icon={<svg viewBox="0 0 20 20"fill="none"width="16"height="16"><path d="M3 10h14M10 3l7 7-7 7"stroke="#00e5cc"strokeWidth="1.4"strokeLinecap="round"strokeLinejoin="round"/></svg>}
        />
        <StatCard
          label="PNL TOTAL BOT"
          value={totalTrades>0?fmtUsd(pnlTotal):'—'}
          sub={`En cours: ${capitalEnCours>0?fmtUsd(capitalEnCours):'—'}`}
          trend={totalTrades>0?(pnlTotal>=0?'accent':'down'):'neutral'}
          icon={<svg viewBox="0 0 20 20"fill="none"width="16"height="16"><path d="M4 10l4 4 8-8"stroke="#00e5cc"strokeWidth="1.4"strokeLinecap="round"strokeLinejoin="round"/></svg>}
          iconColor="success"
        />
        <StatCard
          label="ROI BOT"
          value={totalTrades>0?`${roi>=0?'+':''}${roi.toFixed(1)}%`:'—'}
          sub="Sur capital investi"
          trend={totalTrades>0?(roi>=0?'accent':'down'):'neutral'}
          icon={<svg viewBox="0 0 20 20"fill="none"width="16"height="16"><rect x="2"y="10"width="3"height="8"rx=".5"fill="#00e5cc"/><rect x="7"y="6"width="3"height="12"rx=".5"fill="#00e5cc"opacity=".7"/><rect x="12"y="3"width="3"height="15"rx=".5"fill="#00e5cc"opacity=".5"/></svg>}
          iconColor="pnl"
        />
        <StatCard
          label="WIN RATE BOT"
          value={totalTrades>0?`${winRate.toFixed(1)}%`:'—'}
          sub={totalTrades>0?`Gain moy: ${fmtUsd(gainMoy)}`:'Aucun trade'}
          trend="warning"
          icon={<svg viewBox="0 0 20 20"fill="none"width="16"height="16"><rect x="2"y="4"width="16"height="12"rx="2"stroke="#f0c040"strokeWidth="1.4"/><rect x="6"y="8"width="8"height="4"rx="1"fill="#f0c040"opacity=".5"/></svg>}
          iconColor="warning"
        />
      </div>

      {/* Etat vide */}
      {totalTrades === 0 && (
        <div style={{background:'#0d1018',border:'1px solid #1a1f2a',borderRadius:10,padding:'60px 20px',textAlign:'center',marginBottom:16}}>
          <div style={{fontSize:48,marginBottom:16}}></div>
          <div style={{fontSize:18,fontWeight:700,color:'#e0e4ec',marginBottom:8}}>En attente des trades du bot</div>
          <div style={{fontSize:13,color:'#5a6a80',maxWidth:400,margin:'0 auto',lineHeight:1.7}}>
            Une fois le bot de trading configuré et actif, ses performances apparaîtront ici en temps réel.
          </div>
          <div style={{marginTop:20,display:'flex',gap:12,justifyContent:'center',flexWrap:'wrap'}}>
            {[['','Trades automatiques','Le bot copie les wallets suivis'],['','Analytics en temps réel','PNL, ROI, Win Rate mis à jour'],['','Alertes Telegram','Notification à chaque trade']].map(([icon,title,desc])=>(
              <div key={title} style={{background:'#0a0d14',border:'1px solid #1a2030',borderRadius:8,padding:'16px 20px',maxWidth:200,textAlign:'center'}}>
                <div style={{fontSize:24,marginBottom:8}}>{icon}</div>
                <div style={{fontSize:12,fontWeight:700,color:'#c0c8d8',marginBottom:4}}>{title}</div>
                <div style={{fontSize:11,color:'#5a6a80'}}>{desc}</div>
              </div>
            ))}
          </div>

        </div>
      )}

      {/* Graphique PNL — SIM vs LIVE */}
      {totalTrades > 0 && (pnlCurveSim.length > 1 || pnlCurveLive.length > 1) && (
        <div ref={pnlChartRef} className="chart-fullscreen" style={{background:'#0d1018',border:'1px solid #1a1f2a',borderRadius:10,padding:'18px 20px',marginBottom:16,position:'relative'}}>
          <button onClick={()=>enterFS(pnlChartRef)} title="Plein écran" style={{position:'absolute',top:10,right:14,background:'transparent',border:'1px solid #1a2030',borderRadius:4,color:'#5a6a80',fontSize:11,width:22,height:22,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1}}>⛶</button>
          <div style={{fontSize:12,fontWeight:700,color:'#c0c8d8',marginBottom:12,display:'flex',alignItems:'center',justifyContent:'space-between',flexWrap:'wrap',gap:6}}>
            <div style={{display:'flex',alignItems:'center',gap:7}}>
              <svg viewBox="0 0 14 14"fill="none"width="12"height="12"><path d="M2 10l3-4 3 2 4-6"stroke="#00e5cc"strokeWidth="1.3"strokeLinecap="round"strokeLinejoin="round"/></svg>
              Évolution du PNL Bot
            </div>
            <div style={{display:'flex',gap:14,fontSize:10}}>
              {pnlCurveSim.length>0&&<span style={{color:'#7c4dff',fontWeight:700}}>● SIM {fmtUsd(pnlCurveSim[pnlCurveSim.length-1].cumul)}</span>}
              {pnlCurveLive.length>0&&<span style={{color:'#00e5cc',fontWeight:700}}>● LIVE {fmtUsd(pnlCurveLive[pnlCurveLive.length-1].cumul)}</span>}
            </div>
          </div>
          {(()=>{
            const W=560,H=100,PAD=10;
            const allVals=[...pnlCurveSim,...pnlCurveLive].map(p=>p.cumul);
            const minV=Math.min(...allVals,0),maxV=Math.max(...allVals,0);
            const range=maxV-minV||1;
            const y=v=>PAD+(1-(v-minV)/range)*(H-PAD*2);
            const zeroY=y(0);
            const mkPath=curve=>curve.length<2?null:curve.map((p,i)=>`${i===0?'M':'L'}${(PAD+(i/(curve.length-1))*(W-PAD*2)).toFixed(1)},${y(p.cumul).toFixed(1)}`).join('');
            const pathSim=mkPath(pnlCurveSim);
            const pathLive=mkPath(pnlCurveLive);
            return (
              <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',height:100}}>
                {zeroY>PAD&&zeroY<H-PAD&&<line x1={PAD} y1={zeroY} x2={W-PAD} y2={zeroY} stroke="#1a2030" strokeWidth="1" strokeDasharray="4,3"/>}
                {pathSim&&<path d={pathSim} fill="none" stroke="#7c4dff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.9"/>}
                {pathLive&&<path d={pathLive} fill="none" stroke="#00e5cc" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>}
                {pathSim&&<circle cx={W-PAD} cy={y(pnlCurveSim[pnlCurveSim.length-1].cumul).toFixed(1)} r="3" fill="#7c4dff"/>}
                {pathLive&&<circle cx={W-PAD} cy={y(pnlCurveLive[pnlCurveLive.length-1].cumul).toFixed(1)} r="3" fill="#00e5cc"/>}
              </svg>
            );
          })()}
          <div style={{display:'flex',justifyContent:'space-between',fontSize:10,color:'#3a4a5a',marginTop:4}}>
            <span>{pnlCurve[0]?.date}</span>
            <span style={{color:pnlTotal>=0?'#00e5cc':'#ff6b6b',fontWeight:700}}>PNL net total : {fmtUsd(pnlTotal)}</span>
            <span>{pnlCurve[pnlCurve.length-1]?.date}</span>
          </div>
        </div>
      )}

      {/* Heatmap calendrier */}
      {totalTrades > 0 && (
        <div ref={heatmapRef} className="chart-fullscreen" style={{background:'#0d1018',border:'1px solid #1a1f2a',borderRadius:10,padding:'18px 20px',marginBottom:16,position:'relative'}}>
          <button onClick={()=>enterFS(heatmapRef)} title="Plein écran" style={{position:'absolute',top:10,right:14,background:'transparent',border:'1px solid #1a2030',borderRadius:4,color:'#5a6a80',fontSize:11,width:22,height:22,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1}}>⛶</button>
          {(()=>{
            const now=new Date();
            const year=now.getFullYear(),month=now.getMonth();
            const firstDow=(new Date(year,month,1).getDay()+6)%7;
            const daysInMonth=new Date(year,month+1,0).getDate();
            const monthLabel=now.toLocaleDateString('fr-FR',{month:'long',year:'numeric'});
            const maxAbs=Math.max(...Object.values(dayPnl).map(Math.abs),0.01);
            return (
              <>
                <div style={{fontSize:12,fontWeight:700,color:'#c0c8d8',marginBottom:10,display:'flex',alignItems:'center',gap:7}}>
                  <svg viewBox="0 0 14 14"fill="none"width="12"height="12"><rect x="1"y="2"width="12"height="11"rx="1.5"stroke="#7c4dff"strokeWidth="1.2"/><path d="M4 1v2M10 1v2M1 6h12"stroke="#7c4dff"strokeWidth="1.2"strokeLinecap="round"/></svg>
                  <span style={{textTransform:'capitalize'}}>{monthLabel}</span>
                </div>
                <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',gap:3}}>
                  {['L','M','M','J','V','S','D'].map((l,i)=>(
                    <div key={i} style={{textAlign:'center',fontSize:8,color:'#3a4a5a',paddingBottom:2,fontWeight:600}}>{l}</div>
                  ))}
                  {Array(firstDow).fill(null).map((_,i)=><div key={'e'+i}/>)}
                  {Array.from({length:daysInMonth},(_,i)=>i+1).map(d=>{
                    const key=new Date(year,month,d).toLocaleDateString('fr-FR');
                    const pnl=dayPnl[key]??null;
                    const alpha=pnl!=null?Math.min(0.85,0.2+0.65*(Math.abs(pnl)/maxAbs)):0;
                    const bg=pnl==null?'#0a0d14':pnl>=0?`rgba(0,229,204,${alpha.toFixed(2)})`:`rgba(255,107,107,${alpha.toFixed(2)})`;
                    const isToday=d===now.getDate();
                    return (
                      <div key={d} title={pnl!=null?`${key} : ${pnl>=0?'+':''}${pnl.toFixed(2)}$`:undefined}
                        style={{aspectRatio:'1',borderRadius:3,background:bg,border:`1px solid ${isToday?'#7c4dff':'#1a2030'}`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:8,color:pnl==null?'#2a3040':pnl>=0?'rgba(0,200,180,0.8)':'rgba(255,107,107,0.8)',fontWeight:isToday?700:400}}>
                        {d}
                      </div>
                    );
                  })}
                </div>
              </>
            );
          })()}
        </div>
      )}

      {/* Comparaison semaine en cours vs semaine précédente */}
      {(thisWk.n>0||lastWk.n>0)&&(
        <div style={{background:'#0d1018',border:'1px solid #1a1f2a',borderRadius:10,padding:'18px 20px',marginBottom:16}}>
          <div style={{fontSize:12,fontWeight:700,color:'#c0c8d8',marginBottom:12,display:'flex',alignItems:'center',gap:7}}>
            <svg viewBox="0 0 14 14"fill="none"width="12"height="12"><path d="M7 1v6l3.5 2"stroke="#7c4dff"strokeWidth="1.3"strokeLinecap="round"/><circle cx="7"cy="7"r="6"stroke="#7c4dff"strokeWidth="1.2"/></svg>
            Cette semaine vs Semaine précédente
          </div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12}}>
            {[
              {label:'Trades',  cur:thisWk.n,       prev:lastWk.n,       fv:n=>n,                  fd:d=>`${d>=0?'+':''}${d}`},
              {label:'PNL',     cur:thisWk.pnl,     prev:lastWk.pnl,     fv:fmtUsd,                fd:fmtUsd},
              {label:'Win Rate',cur:thisWk.winRate,  prev:lastWk.winRate, fv:n=>`${n.toFixed(0)}%`, fd:d=>`${d>=0?'+':''}${d.toFixed(0)}%`},
            ].map(({label,cur,prev,fv,fd})=>{
              const diff=cur-prev;
              return (
                <div key={label} style={{background:'#080d14',border:'1px solid #1a2030',borderRadius:8,padding:'12px 14px'}}>
                  <div style={{fontSize:10,color:'#5a6a80',marginBottom:4}}>{label}</div>
                  <div style={{fontSize:16,fontWeight:800,color:'#c0c8d8',marginBottom:4}}>{fv(cur)}</div>
                  {lastWk.n>0
                    ?<div style={{fontSize:10,color:diff>=0?'#00e5cc':'#ff6b6b',fontWeight:600}}>{fd(diff)} vs S-1</div>
                    :<div style={{fontSize:10,color:'#3a4a5a'}}>S-1 : aucune donnée</div>
                  }
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Stats détaillées + Top wallets */}
      {totalTrades > 0 && (
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:16}}>

          {/* Stats détaillées */}
          <div style={{background:'#0d1018',border:'1px solid #1a1f2a',borderRadius:10,padding:'18px 20px'}}>
            <div style={{fontSize:12,fontWeight:700,color:'#c0c8d8',marginBottom:14,display:'flex',alignItems:'center',gap:7}}>
              <svg viewBox="0 0 14 14"fill="none"width="12"height="12"><rect x="1"y="1"width="12"height="12"rx="2"stroke="#00e5cc"strokeWidth="1.2"/><path d="M4 7h6M4 4.5h4M4 9.5h3"stroke="#00e5cc"strokeWidth="1.2"strokeLinecap="round"/></svg>
              Résumé Bot
            </div>
            {[
              ['Trades gagnants', `${gagnants} (${winRate.toFixed(1)}%)`, gagnants > 0 ? 'pos': ''],
              ['Trades perdants', `${perdants}`, perdants > 0 ? 'neg': ''],
              ['Gain moyen', gainMoy > 0 ? fmtUsd(gainMoy) : '—', 'pos'],
              ['Perte moyenne', perteMoy > 0 ? fmtUsd(perteMoy) : '—', 'neg'],
              ['Capital en cours', capitalEnCours > 0 ? fmtUsd(capitalEnCours) : '—', ''],
              ['PNL net', fmtUsd(pnlTotal), pnlTotal >= 0 ? 'pos': 'neg'],
            ].map(([label, val, cls]) => (
              <div key={label} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'8px 0',borderBottom:'1px solid #0d1117',fontSize:12}}>
                <span style={{color:'#5a6a80'}}>{label}</span>
                <span style={{fontWeight:700,color:cls==='pos'?'#00e5cc':cls==='neg'?'#ff6b6b':'#c0c8d8'}}>{val}</span>
              </div>
            ))}
          </div>

          {/* Stats par wallet copié */}
          <div style={{background:'#0d1018',border:'1px solid #1a1f2a',borderRadius:10,padding:'18px 20px'}}>
            <div style={{fontSize:12,fontWeight:700,color:'#c0c8d8',marginBottom:14,display:'flex',alignItems:'center',gap:7}}>
              <svg viewBox="0 0 14 14"fill="none"width="12"height="12"><path d="M7 1l1.5 3.5H12l-2.8 2 1.1 3.5L7 8l-3.3 2 1.1-3.5L2 4.5h3.5z"fill="#f0c040"opacity=".8"/></svg>
              Stats par Wallet Copié
            </div>
            {/* Input hypothétique */}
            <div style={{marginBottom:12,display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
              <div style={{display:'flex',alignItems:'center',gap:6,flex:1,minWidth:180,background:'#080d14',border:'1px solid #1a2030',borderRadius:7,padding:'7px 10px'}}>
                <span style={{fontSize:11,color:'#5a6a80',whiteSpace:'nowrap'}}>Si j'avais investi</span>
                <input
                  type="number" min="0" placeholder="ex: 100"
                  value={hypotheticalAmount}
                  onChange={e => setHypotheticalAmount(e.target.value)}
                  style={{background:'transparent',border:'none',outline:'none',color:'#c0c8d8',fontSize:12,fontWeight:700,width:70,textAlign:'right'}}
                />
                <span style={{fontSize:11,color:'#5a6a80'}}>$ / trade</span>
              </div>
              {parseFloat(hypotheticalAmount) > 0 && (() => {
                const hypoUsd = parseFloat(hypotheticalAmount);
                const hypoTotal = topWallets.reduce((s, [, ws]) => s + ws.roiList.reduce((a, r) => a + r / 100 * hypoUsd, 0), 0);
                return (
                  <span style={{fontSize:11,fontWeight:700,color:hypoTotal>=0?'#00e5cc':'#ff6b6b',background:'rgba(124,77,255,0.08)',border:'1px solid #7c4dff22',borderRadius:6,padding:'5px 10px',whiteSpace:'nowrap'}}>
                    📊 Simulation : {hypoTotal>=0?'+':''}{hypoTotal.toFixed(2)}$
                  </span>
                );
              })()}
            </div>
            {topWallets.length === 0 ? (
              <div style={{textAlign:'center',padding:'30px 0',color:'#3a4a5a',fontSize:12}}>Aucun wallet copié</div>
            ) : (
              <div style={{overflowY:'auto',maxHeight:260}}>
                {topWallets.map(([wallet, stats], i) => {
                  const cfg    = configs.find(c => c.targetWallet === wallet);
                  const winPct = stats.trades > 0 ? ((stats.wins/stats.trades)*100).toFixed(0) : 0;
                  const roi = stats.invested > 0 ? ((stats.pnl/stats.invested)*100).toFixed(1) : 0;
                  const isExpanded    = expandedWallet === wallet;
                  const hypoUsd      = parseFloat(hypotheticalAmount) || 0;
                  const displayPnl   = hypoUsd > 0 ? stats.roiList.reduce((s,r) => s + r / 100 * hypoUsd, 0) : stats.pnl;
                  const displayBest  = hypoUsd > 0 && stats.roiList.length > 0 ? Math.max(...stats.roiList.map(r => r / 100 * hypoUsd)) : stats.bestPnl;
                  const displayWorst = hypoUsd > 0 && stats.roiList.length > 0 ? Math.min(...stats.roiList.map(r => r / 100 * hypoUsd)) : stats.worstPnl;
                  const walletTrades = isExpanded
                    ? filtered.filter(t => t.walletSuivi === wallet).sort((a,b) => (b.sortieTs||0) - (a.sortieTs||0))
                    : [];
                  return (
                    <div key={wallet} style={{borderBottom:'1px solid #0d1117'}}>
                      <div
                        onClick={() => setExpandedWallet(isExpanded ? null : wallet)}
                        style={{padding:'10px 0',cursor:'pointer',userSelect:'none'}}
                      >
                        <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:5}}>
                          <span style={{fontSize:12,flexShrink:0}}>{i===0?'🥇':i===1?'🥈':i===2?'🥉':`#${i+1}`}</span>
                          <span style={{flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                            {cfg
                              ? <><span style={{fontSize:11,fontWeight:700,color:'#c0c8d8'}}>{cfg.emoji ? `${cfg.emoji} ` : ''}{cfg.name}</span><span style={{fontSize:10,color:'#3a4a5a',fontFamily:'monospace',marginLeft:6}}>{wallet.slice(0,6)}…{wallet.slice(-4)}</span></>
                              : <span style={{fontSize:11,fontWeight:700,color:'#c0c8d8',fontFamily:'monospace'}}>{wallet.length>16?`${wallet.slice(0,6)}…${wallet.slice(-4)}`:wallet}</span>
                            }
                          </span>
                          <span style={{fontSize:10,color:'#3a4a5a',flexShrink:0,marginRight:4}}>{isExpanded ? '▲' : '▼'}</span>
                          {onCopyWallet&&<button onClick={e=>{e.stopPropagation();onCopyWallet(wallet);}} title="Créer une config Copytrade" style={{padding:'1px 6px',borderRadius:4,background:'rgba(0,229,204,0.1)',border:'1px solid #00e5cc33',color:'#00e5cc',fontSize:11,fontWeight:800,cursor:'pointer',flexShrink:0,lineHeight:1.4}}>+</button>}
                          <span style={{fontSize:13,fontWeight:700,color:displayPnl>=0?'#00e5cc':'#ff6b6b',flexShrink:0}}>{fmtUsd(displayPnl)}</span>
                        </div>
                        <div style={{display:'flex',gap:10,flexWrap:'wrap'}}>
                          <span style={{fontSize:10,color:'#5a6a80'}}>{stats.trades} trades</span>
                          <span style={{fontSize:10,fontWeight:600,color:Number(winPct)>=50?'#00e5cc':'#ff6b6b'}}>{winPct}% win</span>
                          <span style={{fontSize:10,color:'#5a6a80'}}>ROI <span style={{color:Number(roi)>=0?'#7c4dff':'#ff6b6b',fontWeight:600}}>{Number(roi)>0?'+':''}{roi}%</span></span>
                          {displayBest > -Infinity && <span style={{fontSize:10,color:'#3a4a5a'}}>↑ <span style={{color:'#00e5cc'}}>{fmtUsd(displayBest)}</span></span>}
                          {displayWorst < Infinity && <span style={{fontSize:10,color:'#3a4a5a'}}>↓ <span style={{color:'#ff6b6b'}}>{fmtUsd(displayWorst)}</span></span>}
                        </div>
                      </div>
                      {isExpanded && (
                        <div style={{paddingBottom:8,borderTop:'1px solid #0d1117'}}>
                          {walletTrades.length === 0
                            ? <div style={{fontSize:10,color:'#3a4a5a',padding:'6px 0'}}>Aucun trade dans cette période</div>
                            : walletTrades.map((t, j) => {
                                const cr = t.closeReason;
                                const closeEmoji = cr==='STOP-LOSS'?'🛑':cr==='TAKE-PROFIT'?'🎯':cr==='TRAILING-STOP'?'📉':cr==='MANUAL'?'✋':cr==='PHANTOM-RECONCILE'?'👻':null;
                                const displayTradePnl = hypoUsd > 0 && t.roi != null ? t.roi / 100 * hypoUsd : t.pnlUsd;
                                return (
                                  <div key={t.id||j} style={{display:'flex',alignItems:'center',gap:6,padding:'5px 0 5px 4px',borderTop:'1px solid #080d14',fontSize:11}}>
                                    <span style={{fontWeight:700,color:'#c0c8d8',width:64,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{t.symbol||'—'}</span>
                                    <span style={{fontWeight:700,color:(displayTradePnl||0)>=0?'#00e5cc':'#ff6b6b',width:64,textAlign:'right'}}>{displayTradePnl!=null?`${displayTradePnl>=0?'+':''}${displayTradePnl.toFixed(2)}$`:'—'}</span>
                                    <span style={{color:(t.roi||0)>=0?'#7c4dff':'#ff6b6b',width:48,textAlign:'right'}}>{t.roi!=null?`${(t.roi||0)>=0?'+':''}${t.roi.toFixed(1)}%`:'—'}</span>
                                    {closeEmoji && <span title={cr}>{closeEmoji}</span>}
                                    <span style={{marginLeft:'auto',color:'#3a4a5a',fontSize:10}}>{t.sortieTs?timeAgo(t.sortieTs):t.entreeTs?timeAgo(t.entreeTs):''}</span>
                                  </div>
                                );
                              })
                          }
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Historique des trades */}
      {totalTrades > 0 && (
        <div style={{background:'#0d1018',border:'1px solid #1a1f2a',borderRadius:10,padding:'18px 20px'}}>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14,flexWrap:'wrap',gap:8}}>
            <div style={{fontSize:12,fontWeight:700,color:'#c0c8d8',display:'flex',alignItems:'center',gap:7}}>
              <svg viewBox="0 0 14 14"fill="none"width="12"height="12"><rect x="1"y="1"width="12"height="12"rx="2"stroke="#7c4dff"strokeWidth="1.2"/><path d="M4 7h6M4 4.5h6M4 9.5h4"stroke="#7c4dff"strokeWidth="1.2"strokeLinecap="round"/></svg>
              Historique des Trades Bot
            </div>
            <div style={{display:'flex',gap:6,alignItems:'center'}}>
              <button onClick={exportCSV} style={{padding:'4px 12px',borderRadius:4,fontSize:10,fontWeight:700,cursor:'pointer',background:'rgba(0,229,204,0.08)',border:'1px solid #00e5cc33',color:'#00e5cc',display:'flex',alignItems:'center',gap:5}}>
                <svg viewBox="0 0 12 12"fill="none"width="10"height="10"><path d="M6 1v7M3 5l3 3 3-3M1 10h10"stroke="currentColor"strokeWidth="1.4"strokeLinecap="round"strokeLinejoin="round"/></svg>
                CSV
              </button>
              <div style={{width:1,height:14,background:'#1a2030'}}/>
              {[['date','Date'],['pnl','PNL'],['roi','ROI']].map(([k,l])=>(
                <button key={k} onClick={()=>setSortBy(k)} style={{padding:'4px 10px',borderRadius:4,fontSize:10,fontWeight:600,cursor:'pointer',background:sortBy===k?'rgba(124,77,255,0.15)':'transparent',border:`1px solid ${sortBy===k?'#7c4dff44':'#1a2030'}`,color:sortBy===k?'#7c4dff':'#5a6a80'}}>{l}</button>
              ))}
            </div>
          </div>
          <div style={{overflowX:'auto'}}>
            <table style={{width:'100%',borderCollapse:'collapse'}}>
              <thead>
                <tr style={{borderBottom:'1px solid #1a2030'}}>
                  {['DATE','TOKEN','WALLET COPIÉ','INVESTI','PNL','ROI','RÉSULTAT'].map(h=>(
                    <th key={h} style={{padding:'8px 12px',textAlign:'left',fontSize:9,color:'#3a4a5a',fontWeight:600,textTransform:'uppercase'}}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sorted.map((t,i)=>(
                  <tr key={t.id||i} style={{borderBottom:'1px solid #0d1117'}}>
                    <td style={{padding:'10px 12px',fontSize:11,color:'#5a6a80',whiteSpace:'nowrap'}}>
                      {t.entreeTs?new Date(t.entreeTs*1000).toLocaleDateString('fr-FR',{day:'2-digit',month:'2-digit',year:'2-digit'}):'—'}
                    </td>
                    <td style={{padding:'10px 12px'}}>
                      <div style={{fontSize:12,fontWeight:700,color:'#e0e4ec'}}>{t.symbol||'—'}</div>
                    </td>
                    <td style={{padding:'10px 12px'}}>
                      {t.walletSuivi ? (
                        <div style={{display:'flex',flexDirection:'column',gap:2}}>
                          {(() => { const cfg = configs.find(c => c.targetWallet === t.walletSuivi); return cfg && <span style={{fontSize:11,fontWeight:700,color:'#c0c8d8'}}>{cfg.emoji?`${cfg.emoji} `:''}{cfg.name}</span>; })()}
                          <button
                            onClick={e => { e.stopPropagation(); navigator.clipboard.writeText(t.walletSuivi); setCopiedAddr(t.walletSuivi); setTimeout(()=>setCopiedAddr(null),1500); }}
                            style={{background:'none',border:'none',padding:0,cursor:'pointer',display:'flex',alignItems:'center',gap:4,fontFamily:'monospace',fontSize:10,color:'#7c4dff'}}
                          >
                            {copiedAddr===t.walletSuivi?'✓':`${t.walletSuivi.slice(0,6)}…${t.walletSuivi.slice(-4)}`}
                          </button>
                        </div>
                      ) : '—'}
                    </td>
                    <td style={{padding:'10px 12px',fontSize:12,color:'#c0c8d8'}}>
                      {t.achatUsd>=1000?`$${(t.achatUsd/1000).toFixed(1)}K`:`$${(t.achatUsd||0).toFixed(0)}`}
                    </td>
                    <td style={{padding:'10px 12px',fontSize:12,fontWeight:700,color:(t.pnlUsd||0)>=0?'#00e5cc':'#ff6b6b'}}>
                      {fmtUsd(t.pnlUsd||0)}
                    </td>
                    <td style={{padding:'10px 12px',fontSize:12,fontWeight:700,color:(t.roi||0)>=0?'#00e5cc':'#ff6b6b'}}>
                      {t.roi!=null?`${t.roi>=0?'+':''}${t.roi.toFixed(1)}%`:'—'}
                    </td>
                    <td style={{padding:'10px 12px'}}>
                      <Badge variant={t.resultat==='Gagnant'?'live':'negative'}>
                        {t.resultat||'—'}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}